// Tier-limits editor — third tab on SuperSystemPage.
//
// Three sections (one per module). Each section is a small table with
// one row per tier (Starter / Professional / Enterprise) and one
// inline-editable number cell per limit column. NULL renders as ∞.
// The Enterprise row is read-only (always NULL/unlimited).
//
// Edits commit on blur: if the value changed the row PATCHes the
// module/tier limits row. Success flashes the cell green; failure
// reverts the value and surfaces an error toast (the mutation hook
// owns the toast).
//
// Changes take effect on the next enforcement check — no confirmation
// modal because this is super-admin-only and the audit log already
// captures the change.

import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Card } from '../../components/ui/Card/Card';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import {
  MODULE_KEYS,
  MODULE_TIER_KEYS,
  useModuleTierLimits,
  useUpdateModuleTierLimits,
  type ModuleKey,
  type ModuleTierKey,
  type ModuleTierLimits,
  type ModuleTierLimitsCatalogue,
} from './hooks/useSuper';
import styles from './TierLimitsTab.module.css';

type LimitField = keyof ModuleTierLimits;

interface ColumnSpec {
  field: LimitField;
  label: string;
}

const COLUMNS_BY_MODULE: Record<ModuleKey, ColumnSpec[]> = {
  collections: [
    { field: 'max_staff', label: 'Staff' },
    { field: 'max_drivers', label: 'Drivers' },
    { field: 'max_monthly_jobs', label: 'Jobs/mo' },
    { field: 'max_monthly_messages', label: 'Msgs/mo' },
  ],
  skip: [
    { field: 'max_staff', label: 'Staff' },
    { field: 'max_drivers', label: 'Drivers' },
    { field: 'max_monthly_jobs', label: 'Jobs/mo' },
    { field: 'max_active_inventory', label: 'Active Inventory' },
    { field: 'max_monthly_messages', label: 'Msgs/mo' },
  ],
  weighbridge: [
    { field: 'max_staff', label: 'Staff' },
    { field: 'max_monthly_tickets', label: 'Tickets/mo' },
    { field: 'max_weighbridges', label: 'Weighbridges' },
    { field: 'max_cameras_per_wb', label: 'Cameras/wb' },
    { field: 'max_monthly_messages', label: 'Msgs/mo' },
  ],
};

const MODULE_LABEL: Record<ModuleKey, string> = {
  collections: 'Collections',
  skip: 'Skip',
  weighbridge: 'Weighbridge',
};

const TIER_LABEL: Record<ModuleTierKey, string> = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

/**
 * Parse the inline-edit input. Empty string → null (unlimited).
 * Negative / non-numeric inputs → null (lets the user "clear" a value
 * by deleting it). The server is the authority on what's accepted.
 */
function parseCellValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

interface LimitCellProps {
  module: ModuleKey;
  tier: ModuleTierKey;
  field: LimitField;
  serverValue: number | null;
  disabled?: boolean;
}

function LimitCell({
  module,
  tier,
  field,
  serverValue,
  disabled,
}: LimitCellProps) {
  const update = useUpdateModuleTierLimits();
  const [draft, setDraft] = useState<string>(
    serverValue === null ? '' : String(serverValue),
  );
  const [flash, setFlash] = useState<'success' | null>(null);

  // Reflect server-side updates without stomping in-flight edits.
  useEffect(() => {
    if (!update.isPending) {
      setDraft(serverValue === null ? '' : String(serverValue));
    }
  }, [serverValue, update.isPending]);

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    setDraft(e.target.value);
  }

  async function onBlur() {
    const parsed = parseCellValue(draft);
    if (parsed === serverValue) {
      // No-op — display value already matches the parsed draft.
      setDraft(parsed === null ? '' : String(parsed));
      return;
    }
    try {
      await update.mutateAsync({
        module,
        tier,
        body: { [field]: parsed } as Record<string, number | null>,
      });
      setFlash('success');
      // Flash for 800 ms then clear.
      setTimeout(() => setFlash(null), 800);
    } catch {
      // Revert. The mutation hook surfaces the error toast.
      setDraft(serverValue === null ? '' : String(serverValue));
    }
  }

  if (disabled) {
    return <span className={styles.disabledCell} aria-label="Unlimited">∞</span>;
  }

  return (
    <input
      type="number"
      inputMode="numeric"
      min={0}
      step={1}
      className={`${styles.cellInput} ${flash === 'success' ? styles.cellFlashOk : ''}`}
      value={draft}
      placeholder="∞"
      aria-label={`${MODULE_LABEL[module]} ${TIER_LABEL[tier]} ${field}`}
      disabled={update.isPending}
      onChange={onChange}
      onBlur={onBlur}
    />
  );
}

interface ModuleSectionProps {
  module: ModuleKey;
  data: ModuleTierLimitsCatalogue;
}

function ModuleSection({ module, data }: ModuleSectionProps) {
  const cols = COLUMNS_BY_MODULE[module];
  const rows = MODULE_TIER_KEYS;
  return (
    <section className={styles.section} data-module={module}>
      <h3 className={styles.sectionTitle}>{MODULE_LABEL[module]}</h3>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Tier</th>
              {cols.map((c) => (
                <th key={c.field}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((tier) => {
              // Enterprise is always unlimited (NULL) and read-only — the
              // editor exposes ∞ glyphs so super-admins can't accidentally
              // cap an enterprise tenant by typing into a cell.
              const isEnterprise = tier === 'enterprise';
              const limits = data[module]?.[tier];
              return (
                <tr key={tier} data-tier={tier}>
                  <th scope="row" className={styles.tierLabel}>
                    {TIER_LABEL[tier]}
                  </th>
                  {cols.map((c) => (
                    <td key={c.field}>
                      <LimitCell
                        module={module}
                        tier={tier}
                        field={c.field}
                        serverValue={limits?.[c.field] ?? null}
                        disabled={isEnterprise}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function TierLimitsTab(): JSX.Element {
  const limits = useModuleTierLimits();
  const data = limits.data;

  const sections = useMemo(
    () =>
      MODULE_KEYS.map((m) => ({
        module: m,
        key: `tier-limits-${m}`,
      })),
    [],
  );

  if (limits.isLoading || !data) {
    return (
      <Card>
        <div className={styles.empty}>
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card flush>
      <div className={styles.intro}>
        Edit the tier limits per module. Changes take effect on the next
        enforcement check for every tenant on that tier.
      </div>
      <div className={styles.sections}>
        {sections.map((s) => (
          <ModuleSection key={s.key} module={s.module} data={data} />
        ))}
      </div>
    </Card>
  );
}
